import React from "react";
import { Link } from "react-router-dom";
import TeamLogo, { teamAbbr } from "./TeamLogo";
import { leagueLabel } from "./SBBoardGame";
import { teamThemeColor } from "@/constants/teamThemes";

/* EventScoreboard + MarketTabs (design SBXEventHeader.dc.html, README §2.7).
   A live strip of other live games, a team-colour-washed scoreboard (no lead bar /
   possession / stats), and underline market tabs that only render when they have
   data. */

const hexA = (c, a) => {
  const h = String(c || "#3F3F46").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
function fmtStart(iso) {
  if (!iso) return "Upcoming";
  try { return new Date(iso).toLocaleString(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" }); } catch { return "Upcoming"; }
}

function LiveStrip({ games }) {
  if (!games || !games.length) return null;
  return (
    <div className="no-scrollbar -mr-4 flex gap-2 overflow-x-auto sm:mr-0">
      {games.map((g) => {
        const a = teamAbbr(g.away_team), h = teamAbbr(g.home_team);
        const sa = g.gs_away_score, sh = g.gs_home_score;
        return (
          <Link key={g.id} to={`/event/${g.id}`} className="flex w-[120px] shrink-0 flex-col gap-1.5 rounded-xl p-2 sm:w-[164px]"
            style={{ background: "#111114", boxShadow: "inset 0 0 0 1px #1F1F23" }}>
            {[[g.away_team, a, sa, sh], [g.home_team, h, sh, sa]].map(([name, ab, s, o], i) => (
              <div key={i} className="flex h-[18px] items-center gap-1.5">
                <TeamLogo name={name} league={g.league} sport={g.sport} size={18} />
                <span className="flex-1 truncate" style={{ font: "700 12px Outfit", color: "#D4D4D8" }}>{ab}</span>
                <span style={{ font: "800 13px Manrope", color: Number(s) >= Number(o) ? "#fff" : "#8A8A93", fontVariantNumeric: "tabular-nums" }}>{s ?? ""}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 border-t border-[#1A1A1E] pt-1.5" style={{ font: "700 10px Manrope", color: "#a1a1aa" }}>
              <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />{g.gs_period || "Live"}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function EventScoreboard({ event, liveGames = [] }) {
  const isLive = event.status === "live";
  const final = ["settled", "closed"].includes(event.status);
  const fa = final ? (event.away_score ?? event.gs_away_score) : null;
  const fh = final ? (event.home_score ?? event.gs_home_score) : null;
  const showScore = (isLive && event.gs_home_score != null) || (final && fh != null);
  const liveScore = showScore; // reuse the score block
  const sa = isLive ? event.gs_away_score : fa;
  const sh = isLive ? event.gs_home_score : fh;
  const wa = hexA(teamThemeColor({ team: event.away_team, league: event.league, sport: event.sport }), 0.26);
  const wh = hexA(teamThemeColor({ team: event.home_team, league: event.league, sport: event.sport }), 0.26);
  const sideCaption = (lbl) => <div className="mt-1 hidden text-center sm:block sm:text-left" style={{ font: "600 12px Outfit", letterSpacing: ".12em", color: "#71717a" }}>{lbl}</div>;
  const short = (n) => { const w = String(n || "").trim().split(/\s+/); return w.length > 1 ? w[w.length - 1] : (w[0] || ""); };
  return (
    <div className="flex flex-col gap-3 sm:gap-4" data-testid="event-scoreboard">
      <LiveStrip games={liveGames} />
      <div className="relative overflow-hidden rounded-[20px] sm:rounded-[24px]" style={{ background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23, inset 0 1px 0 rgba(255,255,255,.06), 0 20px 50px rgba(0,0,0,.35)" }}>
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 140% at 0% 50%, ${wa} 0%, transparent 72%), radial-gradient(60% 140% at 100% 50%, ${wh} 0%, transparent 72%)` }} />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg,transparent,rgba(255,255,255,.12),transparent)" }} />
        <div className="relative flex items-center justify-between px-4 pt-3.5 sm:px-7 sm:pt-5">
          <span style={{ font: "800 11px Outfit", letterSpacing: ".2em", color: "#a1a1aa" }}>{(leagueLabel(event.league) || event.sport || "").toUpperCase()}</span>
          {isLive ? (
            <span className="inline-flex h-6 items-center gap-1.5 rounded-[7px] px-2.5" style={{ background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.35)", font: "800 11px Outfit", letterSpacing: ".14em", color: "#EF4444" }}>
              <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444]" style={{ animation: "pnrPulse 1.6s ease-out infinite" }} />LIVE
            </span>
          ) : final ? (
            <span className="inline-flex h-6 items-center rounded-[7px] px-2.5" style={{ background: "#1F1F23", font: "800 11px Outfit", letterSpacing: ".14em", color: "#a1a1aa" }}>FINAL</span>
          ) : <span style={{ font: "600 13px Outfit", color: "#a1a1aa" }}>{fmtStart(event.start_time)}</span>}
        </div>
        <div className="relative grid items-center gap-2 px-3.5 pb-4 pt-3 sm:gap-6 sm:px-8 sm:pb-[30px] sm:pt-3.5" style={{ gridTemplateColumns: "minmax(0,1fr) auto minmax(0,1fr)" }}>
          <div className="flex min-w-0 flex-col items-center gap-2 sm:flex-row sm:gap-4">
            <TeamLogo name={event.away_team} league={event.league} sport={event.sport} size={44} className="sm:!h-16 sm:!w-16" />
            <div className="w-full min-w-0 text-center sm:w-auto sm:text-left">
              <div className="truncate font-display font-extrabold leading-[1.1] tracking-tight text-[14px] sm:text-[22px]">
                <span className="sm:hidden">{short(event.away_team)}</span><span className="hidden sm:inline">{event.away_team}</span>
              </div>
              {sideCaption("AWAY")}
            </div>
          </div>
          <div className="flex flex-col items-center gap-1.5 sm:gap-2.5">
            {liveScore ? (
              <div className="flex items-center gap-2.5 font-display font-extrabold tabular-nums sm:gap-[18px]" style={{ letterSpacing: "-.05em" }} data-testid="event-live-score">
                <span className="text-[38px] sm:text-[60px]" style={{ color: Number(sa) >= Number(sh) ? "#fff" : "#8A8A93" }}>{sa}</span>
                <span className="h-[3px] w-2.5 rounded bg-[#3F3F46] sm:w-3.5" />
                <span className="text-[38px] sm:text-[60px]" style={{ color: Number(sh) >= Number(sa) ? "#fff" : "#8A8A93" }}>{sh}</span>
              </div>
            ) : <span className="font-display font-extrabold text-[22px] text-[#3F3F46] sm:text-[30px]">vs</span>}
            {/* live period always; pregame time on desktop only (row 1 already shows it on mobile) */}
            <span style={{ font: "700 13px Manrope", color: "#E4E4E7", fontVariantNumeric: "tabular-nums" }} className={`sm:text-[16px] ${isLive || final ? "" : "hidden sm:block"}`}>{isLive ? (event.gs_period || "") : final ? "Final" : fmtStart(event.start_time)}</span>
          </div>
          <div className="flex min-w-0 flex-col-reverse items-center gap-2 sm:flex-row sm:gap-4">
            <div className="w-full min-w-0 text-center sm:w-auto sm:text-right">
              <div className="truncate font-display font-extrabold leading-[1.1] tracking-tight text-[14px] sm:text-[22px]">
                <span className="sm:hidden">{short(event.home_team)}</span><span className="hidden sm:inline">{event.home_team}</span>
              </div>
              {sideCaption("HOME")}
            </div>
            <TeamLogo name={event.home_team} league={event.league} sport={event.sport} size={44} className="sm:!h-16 sm:!w-16" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function MarketTabs({ tabs, active, onChange }) {
  return (
    <div className="no-scrollbar -mr-4 flex gap-5 overflow-x-auto border-b border-[#1F1F23] sm:mr-0 sm:gap-7">
      {tabs.map((t) => {
        const on = t.key === active;
        return (
          <button key={t.key} type="button" onClick={() => onChange(t.key)} data-testid={t.testid}
            className="flex h-[46px] shrink-0 items-center gap-1.5 whitespace-nowrap sm:h-[50px]"
            style={on
              ? { font: "700 15px Outfit", color: "#fff", boxShadow: "inset 0 -2px 0 #00D632" }
              : { font: "600 15px Outfit", color: "#8A8A93" }}>
            {t.label}
            {t.count != null && <span style={{ font: "700 12px Manrope", color: on ? "#71717a" : "#52525b" }}>{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
